import {
  type Boundary,
  type ExternalBounded,
  type ExternalMax,
  type ExternalMin,
  type FullyBounded,
  type InternalBounded,
  type PartialMaybeDomain,
} from './types';

export function isMetricKeyAbsoluteRuntime(metricKey: string) {
  return metricKey === '_absolute_runtime';
}

export function makeSafeZoom(z: Record<string, number | null | undefined>) {
  return Object.entries(z).reduce(
    (acc, [key, val]) => {
      if (typeof val === 'number' && Number.isFinite(val)) {
        acc[key] = val;
      } else {
        acc[key] = null;
      }
      return acc;
    },
    {} as Record<string, number | null>
  );
}

export function isInRange(
  point: number | string,
  // two null domains have been filtered out above
  [min, max]: PartialMaybeDomain
) {
  if (typeof point !== 'number') {
    // NaN, Infinity, -Infinity come in as strings
    return false;
  }
  if (min == null && max !== null) {
    return point <= max;
  }
  if (max == null) {
    return point >= min;
  }
  return point >= min && point <= max;
}

export function hasLogicalMaxBoundary(b: ExternalMax) {
  return b.externalMax > b.internalMax;
}

export function hasLogicalMinBoundary(b: ExternalMin) {
  return b.externalMin < b.internalMin;
}

export function hasMinBoundary(b: InternalBounded): b is ExternalMin {
  return Number.isFinite(b.externalMin);
}

export function hasMaxBoundary(b: InternalBounded): b is ExternalMax {
  return Number.isFinite(b.externalMax);
}

export function hasLogicalBoundaries(b: FullyBounded) {
  return hasLogicalMinBoundary(b) && hasLogicalMaxBoundary(b);
}

export function hasInternalBounds(b: Boundary): b is InternalBounded {
  return Number.isFinite(b.internalMax) && Number.isFinite(b.internalMin);
}

export function isMissingExternalBounds(b: Boundary) {
  return b.externalMin === null && b.externalMax === null;
}

export function hasExternalBounds(b: InternalBounded): b is FullyBounded;
export function hasExternalBounds(b: Boundary): b is ExternalBounded;
export function hasExternalBounds(b: Boundary): boolean {
  return Number.isFinite(b.externalMax) && Number.isFinite(b.externalMin);
}

export function getConfigBoundedZoom(
  configMin: number | null,
  configMax: number | null,
  queryMin: number | null,
  queryMax: number | null
) {
  if (configMin == null && configMax == null) {
    return {
      min: queryMin ?? null,
      max: queryMax ?? null,
    };
  }

  // if the minimum query is out of bounds return the config values
  if (configMax != null && queryMin != null && queryMin > configMax) {
    return {
      min: configMin ?? null,
      max: configMax ?? null,
    };
  }

  // if the maximum query is out of bounds return the config
  if (configMin != null && queryMax != null && queryMax < configMin) {
    return {
      min: configMin ?? null,
      max: configMax ?? null,
    };
  }

  const min = Math.max(queryMin ?? -Infinity, configMin ?? -Infinity);
  const max = Math.min(queryMax ?? Infinity, configMax ?? Infinity);

  return {
    min: min === -Infinity ? null : min,
    max: max === Infinity ? null : max,
  };
}
